// Libraries
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// Store
import { RootState } from '../../app/store';

// API
import { fetchSession, pushBingoClaim } from './sessionAPI';

// Models
import { Session, CurrentGame, Winner } from '../session/sessionModels';

/**
 * @interface SessionState
 * Describes the properties for the global session state
 *
 * @property {Session} session Currently entered session
 * @property {string} status Current data fetching status
 */
export interface SessionState {
    session: Session | null;
    currentGame: CurrentGame;
    currentMacro: number;
    currentBanner: number;
    players: {
        id: number;
        username: string;
    }[];
    gameWinners: Winner[];
    accumulatedWinners: {
        id: number;
        username: string;
        points: number;
        patterns: number;
    }[];
    error: string;
    status: 'idle' | 'loading' | 'failed';
}

/**
 * @const initialGameState
 * Describes the initial state of the game on application start up
 */
const initialGameState: CurrentGame = {
    id: 0,
    state: 'pending',
    start: '',
    ball: 0,
    call: 0,
    called: [],
    currentPot: [],
    patternsWon: [],
    winners: [],
    manualDaubList: [],
    manualClaimState: 'pending',
};

/**
 * @const initialState
 * Describes the initial session state on application start up
 */
const initialState: SessionState = {
    session: null,
    currentGame: initialGameState,
    currentMacro: 0,
    currentBanner: 0,
    players: [],
    gameWinners: [],
    accumulatedWinners: [],
    error: '',
    status: 'idle',
};

/**
 * @function fetchSessionAsync
 * Fetches current session
 * https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * @param {number} userId The current user's ID to authenticate
 *
 * @return {object} Object containing detailed session data
 */
export const fetchSessionAsync = createAsyncThunk<
    // Return type of the payload creator
    any,
    // First argument to the payload creator
    number,
    // Optional fields for defining thunkApi field types
    { rejectValue: string }
>('session/fetchSession', async (sessionId: number, { rejectWithValue }) => {
    try {
        const response = await fetchSession(sessionId);
        return response;
    } catch (error: any) {
        return rejectWithValue(error);
    }
});

/**
 * @function postBingoClaimAsync
 * Submits a bingo claim on a single pattern
 * https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * @param {number} gameId Game to assign claim to
 * @param {number} cards List of unique identifiers for each card
 * @param {number} patternId Unique identifier for pattern matched
 *
 * @return {any}
 */
export const postBingoClaimAsync = createAsyncThunk<
    // Return type of the payload creator
    any,
    // First argument to the payload creator
    { gameId: number; cards: number[]; patternId: number },
    // Optional fields for defining thunkApi field types
    { rejectValue: string }
>(
    'session/pushBingoClaim',
    async ({ gameId, cards, patternId }, { rejectWithValue }) => {
        try {
            const response = await pushBingoClaim(gameId, cards, patternId);
            return response;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    },
);

/**
 * @const sessionSlice
 * Describes initial state and reducer functions to handle session state changes
 * https://redux-toolkit.js.org/api/createslice
 */
export const sessionSlice = createSlice({
    name: 'session',
    initialState,
    reducers: {
        pushSessionState: (state, action) => {
            if (state.session !== null) {
                state.session = { ...state.session, ...action.payload };
            }
        },
        pushSessionWinners: (state, action) => {
            state.gameWinners = action.payload.gameWinners;
            state.accumulatedWinners = action.payload.accumulatedWinners;
        },
        pushGameState: (state, action) => {
            if (state.session !== null && state.currentGame !== null) {
                state.currentGame = {
                    ...state.currentGame,
                    ...action.payload,
                };
            }
        },
        pushWinningPattern: (state, action) => {
            if (!state.currentGame.patternsWon.includes(action.payload)) {
                state.currentGame.patternsWon = [
                    ...state.currentGame.patternsWon,
                    action.payload.pattern,
                ];
            }
        },
        pushManualDaub: (state, action) => {
            const daubedCard = state.currentGame.manualDaubList.find(
                ({ cardId }) => cardId === action.payload.id,
            );

            if (daubedCard !== undefined) {
                state.currentGame.manualDaubList =
                    state.currentGame.manualDaubList.map(
                        ({ cardId, numbers }) => {
                            let newNumberList: number[] = numbers;

                            if (cardId === action.payload.id) {
                                if (numbers.includes(action.payload.number)) {
                                    newNumberList = numbers.filter(
                                        (number) =>
                                            number !== action.payload.number,
                                    );
                                } else {
                                    newNumberList = [
                                        ...numbers,
                                        action.payload.number,
                                    ];
                                }
                            }

                            return {
                                cardId: cardId,
                                numbers: newNumberList,
                            };
                        },
                    );
            } else {
                state.currentGame.manualDaubList = [
                    ...state.currentGame.manualDaubList,
                    {
                        cardId: action.payload.id,
                        numbers: [action.payload.number],
                    },
                ];
            }
        },
        pushCurrentMacro: (state, action) => {
            state.currentMacro = action.payload;
        },
        pushCurrentBanner: (state, action) => {
            state.currentBanner = action.payload;
        },
        pushPlayerList: (state, action) => {
            state.players = action.payload.players;
        },
        clearGame: (state) => {
            state.currentGame = initialGameState;
        },
        clearSession: (state) => {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSessionAsync.pending, (state) => {
                state.status = 'loading';
                state.error = '';
            })
            .addCase(fetchSessionAsync.fulfilled, (state, action) => {
                state.status = 'idle';
                state.error = '';
                state.session = { ...action.payload, state: 'pending' };
            })
            .addCase(fetchSessionAsync.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload
                    ? action.payload
                    : 'We are currently experiencing issues';
            })
            .addCase(postBingoClaimAsync.pending, (state) => {
                state.status = 'loading';
                state.error = '';
            })
            .addCase(postBingoClaimAsync.fulfilled, (state, action) => {
                state.status = 'idle';
                state.error = '';
                state.currentGame = { ...state.currentGame, ...action.payload };
            })
            .addCase(postBingoClaimAsync.rejected, (state) => {
                state.status = 'failed';
                state.error = '';
            });
    },
});

/**
 * @const selectSession
 * Exposes currently joined session in session state for use in rest of application
 */
export const selectSession = (state: RootState) => state.session.session;

/**
 * @const selectSessionWinners
 * Exposes winners of session in session state for use in rest of application
 */
export const selectSessionWinners = (state: RootState) => {
    return {
        gameWinners: state.session.gameWinners,
        accumulatedWinners: state.session.accumulatedWinners,
    };
};

/**
 * @const selectPlayerList
 * Exposes currently active players in session state for use in rest of application
 */
export const selectPlayerList = (state: RootState) => state.session.players;

/**
 * @const selectCurrentGame
 * Exposes currently active game in session state for use in rest of application
 */
export const selectCurrentGame = (state: RootState) =>
    state.session.currentGame;

/**
 * @const selectCurrentMacro
 * Exposes currently active macro in session state for use in rest of application
 */
export const selectCurrentMacro = (state: RootState) =>
    state.session.currentMacro;

/**
 * @const selectCurrentBanner
 * Exposes currently active banner in session state for use in rest of application
 */
export const selectCurrentBanner = (state: RootState) =>
    state.session.currentBanner;

/**
 * @const selectSessionStatus
 * Exposes current fetch status of session data for use in rest of application
 */
export const selectSessionStatus = (state: RootState) => state.session.status;

/**
 * @const selectSessionError
 * Exposes current fetch error of session data for use in rest of application
 */
export const selectSessionError = (state: RootState) => state.session.error;

/**
 * @const pushSessionState
 * Exposes pushSessionState reducer for use in rest of application
 */
export const { pushSessionState } = sessionSlice.actions;

/**
 * @const pushSessionWinners
 * Exposes pushSessionWinners reducer for use in rest of application
 */
export const { pushSessionWinners } = sessionSlice.actions;

/**
 * @const pushGameState
 * Exposes pushGameState reducer for use in rest of application
 */
export const { pushGameState } = sessionSlice.actions;

/**
 * @const pushWinningPattern
 * Exposes pushWinningPattern reducer for use in rest of application
 */
export const { pushWinningPattern } = sessionSlice.actions;

/**
 * @const pushManualDaub
 * Exposes pushManualDaub reducer for use in rest of application
 */
export const { pushManualDaub } = sessionSlice.actions;

/**
 * @const pushCurrentMacro
 * Exposes pushCurrentMacro reducer for use in rest of application
 */
export const { pushCurrentMacro } = sessionSlice.actions;

/**
 * @const pushCurrentBanner
 * Exposes pushCurrentBanner reducer for use in rest of application
 */
export const { pushCurrentBanner } = sessionSlice.actions;

/**
 * @const pushPlayerList
 * Exposes pushPlayerList reducer for use in rest of application
 */
export const { pushPlayerList } = sessionSlice.actions;

/**
 * @const clearSession
 * Exposes clearSession reducer for use in rest of application
 */
export const { clearSession } = sessionSlice.actions;

/**
 * @const clearGame
 * Exposes clearGame reducer for use in rest of application
 */
export const { clearGame } = sessionSlice.actions;

/**
 * @const sessionSlice.reducer
 * Exposes session reducer for use in store
 */
export default sessionSlice.reducer;
